import {useEffect, useMemo, useState} from 'react';
import {ANALYTICS_CHANNEL, CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_PAID_MEDIA} from '../../channels/channels';
import {fetchAccountRule} from '../../ga4_attribution/ga4AttributionApi';
import {
    ATTRIBUTION_CHANNELS, LEVEL_AD, LEVEL_ADGROUP, isAutomatic, ruleFromPayload,
} from '../../ga4_attribution/attributionModel';
import {isOk} from '../../../utils/apiResponse';

/**
 * ¿Con qué niveles cruza el GA4 de un cliente con su pauta? (frente `informes-aviso-ga4`, 20/10)
 *
 * Es la parte del cruce por grupo y anuncio que el editor puede saber ANTES de calcular: la regla de
 * etiquetado de cada cuenta de pauta del cliente, la misma que el backend le da al nodo
 * (`ga4_ads_rule` del config). Google cruza solo, por ID; Meta, TikTok, LinkedIn y DV360, según la
 * regla de la agencia o la propia de la cuenta (`GET client/ad_account/<id>/ga4-attribution-rule`,
 * que lee la base y nada más). Lo usa el aviso de `ga4Split.js`.
 *
 * Un nivel cruza sólo si cruza en TODAS las cuentas de pauta del cliente: el GA4 de una cuenta sin
 * regla se sigue partiendo, y el aviso tiene que seguir saliendo. Cuentan como sin regla una cuenta
 * cuya regla no se pudo leer (red, 404 de un backend viejo o de un usuario sin acceso a la cuenta) y
 * una de pauta que no tiene regla posible: la Planilla, o un canal que este bundle no conoce (el
 * vínculo de GA4 empareja cualquier campaña de pauta, y la de una cuenta sin regla queda partida).
 * Sin GA4 en el cliente, ningún nivel: no hay nada que cruzar.
 */

/** La regla que vale para la cuenta según el GET: la propia si la tiene, si no la de la agencia. */
export function effectiveRuleOf(payload) {
    if (!payload || payload.supported === false) return null;
    const raw = payload.mode === 'custom' && payload.custom_rule ? payload.custom_rule : payload.agency_rule;
    if (!raw) return null;
    // `mode` es el de Google (`AUTO_GOOGLE_ADS_LINK`): `ruleFromPayload` sólo lee los parámetros.
    return {...ruleFromPayload(raw), mode: raw.mode};
}

function crossesLevel({channel, rule}, level) {
    if (isAutomatic(channel, rule)) return true;
    if (!rule) return false;
    return Boolean(level === LEVEL_ADGROUP ? rule.adgroup_param : rule.ad_param);
}

/** Los niveles que cruzan todas las cuentas: `accountRules` = `[{channel, rule}]`, `rule` `null` si no se leyó. */
export function levelsCrossedByAll(accountRules) {
    const list = accountRules || [];
    if (!list.length) return [];
    return [LEVEL_ADGROUP, LEVEL_AD].filter((level) => list.every((account) => crossesLevel(account, level)));
}

/** ¿Es de pauta? Las de la familia de pauta y, ante la duda (un canal que el bundle no conoce), también. */
function isPaidAccount(account) {
    if (account.channel === ANALYTICS_CHANNEL) return false;
    const info = CHANNEL_INFO_BY_CHANNEL[account.channel];
    return !info || info.channelType === CHANNEL_TYPE_PAID_MEDIA;
}

/** Las cuentas de pauta del cliente (`ad_accounts` del form-data), o `[]` si no tiene GA4. */
export function crossAccountsOf(adAccounts, agencyClientId) {
    if (agencyClientId === null || agencyClientId === undefined || agencyClientId === '') return [];
    const ofClient = (adAccounts || []).filter((aa) => String(aa.agency_client_id) === String(agencyClientId));
    if (!ofClient.some((aa) => aa.channel === ANALYTICS_CHANNEL)) return [];
    return ofClient.filter(isPaidAccount);
}

function ruleOfAccount(account) {
    // Google cruza solo, y una cuenta sin regla posible no tiene nada que pedir.
    if (isAutomatic(account.channel) || !ATTRIBUTION_CHANNELS.includes(account.channel)) {
        return Promise.resolve({channel: account.channel, rule: null});
    }
    return fetchAccountRule(account.agency_client_ad_account_id)
        .then((response) => ({
            channel: account.channel,
            rule: isOk(response) ? effectiveRuleOf(response.data) : null,
        }))
        .catch(() => ({channel: account.channel, rule: null}));
}

/**
 * `levels`: los niveles que cruzan (`'adgroup'`, `'ad'`). Mientras se leen las reglas, `[]`: el aviso
 * sale como antes del cruce. Una respuesta del cliente anterior no pisa la del actual.
 */
export function useGa4CrossLevels(adAccounts, agencyClientId) {
    const accounts = useMemo(() => crossAccountsOf(adAccounts, agencyClientId), [adAccounts, agencyClientId]);
    const key = accounts.map((aa) => `${aa.channel}:${aa.agency_client_ad_account_id}`).join('|');
    const [result, setResult] = useState({key: null, levels: []});

    useEffect(() => {
        if (!accounts.length) {
            setResult({key, levels: []});
            return undefined;
        }
        let alive = true;
        Promise.all(accounts.map(ruleOfAccount)).then((rules) => {
            if (alive) setResult({key, levels: levelsCrossedByAll(rules)});
        });
        return () => { alive = false; };
        // `key` resume `accounts`: un form-data nuevo con las mismas cuentas no vuelve a pedir.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    return result.key === key ? result.levels : [];
}
